import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { organizationService, planService, menuAdminService } from '../services';
import { withoutManagerOnlyMenus, withoutCareHomeOnlyMenus, CARE_HOME_ONLY_MENU_CODES } from '../utils/menuTree';
import { AlertCircle, ArrowLeft, Check, Plus, Trash2, Building2, Bed, Info, Save, ChevronRight, ChevronLeft, Loader, Layout } from 'lucide-react';
import { Button, Select } from '../components';
import { pinProblem } from '../utils/pin';

/**
 * Create/Edit Organization Page
 * Multi-Step Wizard Form - Updated with Plan selection
 */
const CreateOrganization = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  // Wizard State
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    organization_type: 'carehome',
    address: '',
    phone: '',
    email: '',
    cqc_registration_number: '',
    setup_type: 'single',
    residential_beds: 0,
    nursing_beds: 0,
    dementia_beds: 0,
    current_residents: 0,
    current_staff: 0,
    primary_care_category: '',
    current_service_users: 0,
    care_workers: 0,
    avg_visits_per_day: 0,
    operating_area: '',
    visit_model: '',
    plan_id: '',
    subscription_status: 'trial',
    trial_start_date: '',
    trial_end_date: '',
    billing_cycle: 'monthly',
    selectedMenuIds: [],
  });

  const [plans, setPlans] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);

  // The organisation's ONE account owner. The admin creates only the owner;
  // the owner creates managers from Staff Management, and managers create
  // staff. Their login id is generated by the API (OCH1001+ / OHC1001+) — the
  // one shown here is a preview fetched for the chosen sector.
  // Multi Site Group only: the group's sites, Site 1 first. Site 1 is the
  // organisation itself; every other site becomes its own record that the
  // owner and their managers switch between from the top bar. Sites are never
  // deleted once saved — they are switched off, keeping their records.
  const [sites, setSites] = useState([]);
  const blankSite = () => ({ id: null, name: '', is_active: true });

  const [owner, setOwner] = useState({
    id: null, user_id: '', pin: '', name: '', email: '', phone: '',
    creation_type: 'manual', pin_set: false, send_link: false,
  });

  const steps = [
    { id: 1, label: 'Identity', desc: 'Basic info & location' },
    { id: 2, label: 'Operations', desc: 'Capacity & care types' },
    { id: 3, label: 'Plan & Module', desc: 'Subscription & Access' },
    { id: 4, label: 'Account Owner', desc: 'Who owns the organisation' },
  ];

  useEffect(() => {
    fetchPlans();
    fetchModules();
    if (isEditMode) {
      fetchOrganization();
    }
  }, [id]);

  // Auto-hide error
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const fetchPlans = async () => {
    try {
      const response = await planService.getAllPlans();
      if (response.success) {
        setPlans(response.data);
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
    }
  };

  const fetchModules = async () => {
    try {
      const response = await menuAdminService.getAllMenus(1, 100, false);
      if (response.success) {
        // An organisation is granted whole modules. The Staff module's own
        // sub-menus (Recruitment, Staff Compliance, Payroll) are not offered
        // here — a manager assigns those to their staff on the Add Staff form.
        setAvailableModules(withoutManagerOnlyMenus(response.data));
      }
    } catch (err) {
      console.error('Error fetching modules:', err);
    }
  };

  const fetchOrganization = async () => {
    try {
      setLoading(true);
      const response = await organizationService.getOrganizationById(id);
      if (response.success && response.data) {
        const org = response.data;
        const users = org.Users || [];
        // The organisation's modules live on its leaders' menu rows: the owner's
        // first, else a manager's (an organisation from before owners).
        const leader = org.owner
          || (org.managers || [])[0]
          || users.find((u) => u.UserMenu?.menu_ids || u.userMenu?.menu_ids || u.MenuUser?.menu_ids);
        const leaderRow = leader ? users.find((u) => u.id === leader.id) || leader : null;
        let menuIds = leaderRow
          ? (leaderRow.UserMenu?.menu_ids || leaderRow.userMenu?.menu_ids || leaderRow.MenuUser?.menu_ids || [])
          : [];

        // Ensure it's an array (sometimes JSON is returned as string)
        if (typeof menuIds === 'string') {
          try {
            menuIds = JSON.parse(menuIds);
          } catch (e) {
            menuIds = [];
          }
        }
        if (!Array.isArray(menuIds)) menuIds = [];

        setFormData(prev => ({
          ...prev,
          name: org.name || '',
          organization_type: org.organization_type || 'carehome',
          address: org.address || '',
          phone: org.phone || '',
          email: org.email || '',
          cqc_registration_number: org.cqc_registration_number || '',
          setup_type: org.setup_type || 'single',
          residential_beds: org.residential_beds || 0,
          nursing_beds: org.nursing_beds || 0,
          dementia_beds: org.dementia_beds || 0,
          current_residents: org.current_residents || 0,
          current_staff: org.current_staff || 0,
          primary_care_category: org.primary_care_category || '',
          current_service_users: org.current_service_users || 0,
          care_workers: org.care_workers || 0,
          avg_visits_per_day: org.avg_visits_per_day || 0,
          operating_area: org.operating_area || '',
          visit_model: org.visit_model || '',
          plan_id: org.activePlan?.plan_id || '',
          subscription_status: org.activePlan?.subscription_status || 'trial',
          trial_start_date: org.activePlan?.trial_start_date ? org.activePlan.trial_start_date.split('T')[0] : '',
          trial_end_date: org.activePlan?.trial_end_date ? org.activePlan.trial_end_date.split('T')[0] : '',
          billing_cycle: org.activePlan?.billing_cycle || 'monthly',
          selectedMenuIds: menuIds
        }));

        if (Array.isArray(org.sites) && org.setup_type === 'multi') {
          setSites(org.sites.map((site) => ({ id: site.id, name: site.name || '', is_active: site.is_active !== false })));
        }

        if (org.owner) {
          setOwner({
            id: org.owner.id,
            user_id: org.owner.user_id,
            pin: '',
            name: org.owner.name || '',
            email: org.owner.email || '',
            phone: org.owner.phone || '',
            creation_type: 'manual',
            pin_set: org.owner.pin_set === true,
            send_link: false,
          });
        }
      } else {
        setError('Organization not found');
      }
    } catch (err) {
      console.error('Error fetching organization:', err);
      setError('Failed to load organization');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value,
    }));
    setError('');
  };

  const handleModuleToggle = (moduleId) => {
    setFormData(prev => {
      const current = prev.selectedMenuIds || [];
      const updated = current.includes(moduleId)
        ? current.filter(id => id !== moduleId)
        : [...current, moduleId];
      return { ...prev, selectedMenuIds: updated };
    });
  };

  const handleOwnerChange = (e) => {
    const { name, value } = e.target;
    setOwner((prev) => ({ ...prev, [name]: value }));
  };

  // Preview the owner's login id for the chosen sector (OCH1001+ for a care
  // home, OHC1001+ for domiciliary). An existing owner keeps theirs.
  useEffect(() => {
    if (loading || currentStep !== 4 || owner.id || !formData.organization_type) return;
    organizationService.getNextUserId(formData.organization_type)
      .then((res) => {
        if (res?.success && res.data?.user_id) {
          setOwner((prev) => (prev.id ? prev : { ...prev, user_id: res.data.user_id }));
        }
      })
      .catch((err) => console.error('Failed to fetch next owner id:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, formData.organization_type, loading, owner.id]);

  // Switching to Multi Site Group starts the list with two empty sites.
  const handleSetupTypeChange = (value) => {
    setFormData((prev) => ({ ...prev, setup_type: value }));
    if (value === 'multi' && sites.length === 0) {
      setSites([{ ...blankSite(), name: formData.name || '' }, blankSite()]);
    }
    setError('');
  };

  const updateSite = (index, changes) => {
    setSites((prev) => prev.map((site, i) => (i === index ? { ...site, ...changes } : site)));
    setError('');
  };

  // Every site named and names unique. Returns a message, or null when the
  // list is fine. One site is allowed: the switcher simply appears once a
  // second is added (an organisation marked "multi" before sites existed has
  // just its main site).
  const sitesProblem = () => {
    if (formData.setup_type !== 'multi') return null;
    if (!sites.length) return 'Add at least one site.';
    if (sites.some((site) => !site.name.trim())) return 'Give every site a name.';
    const names = sites.map((site) => site.name.trim().toLowerCase());
    if (new Set(names).size !== names.length) return 'Two sites have the same name. Give each site its own name.';
    return null;
  };

  const handleNext = () => {
    if (currentStep === 1) {
       if (!formData.name.trim() || !formData.email.trim()) {
          setError('Please fill required fields: Name and Email');
          return;
       }
       const siteIssue = sitesProblem();
       if (siteIssue) {
          setError(siteIssue);
          return;
       }
    }
    if (currentStep === 2) {
      if (formData.organization_type === 'domiciliary' && !formData.current_service_users) {
        setError('Please specify current service users');
        return;
      }
    }
    if (currentStep === 3) {
      if (!formData.plan_id) {
        setError('Please select a subscription plan');
        return;
      }
      if (!formData.selectedMenuIds || formData.selectedMenuIds.length === 0) {
        setError('Please select at least one module access');
        return;
      }
      if (formData.subscription_status === 'trial') {
        if (!formData.trial_start_date || !formData.trial_end_date) {
          setError('Please provide trial start and end dates');
          return;
        }
      } else if (formData.subscription_status === 'active' && !formData.billing_cycle) {
        setError('Please select a billing cycle');
        return;
      }
    }
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      setError('');
      window.scrollTo(0, 0);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError('');
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    // Final Validation
    if (!formData.name.trim() || !formData.email.trim()) {
      setError('Basic details (Name, Email) are required');
      setCurrentStep(1);
      return;
    }

    if (!formData.plan_id || !formData.selectedMenuIds || formData.selectedMenuIds.length === 0) {
      setError('Step 3: Please select a subscription plan and at least one module');
      setCurrentStep(3);
      return;
    }

    if (formData.subscription_status === 'trial') {
      if (!formData.trial_start_date || !formData.trial_end_date) {
        setError('Step 3: Trial start and end dates are required');
        setCurrentStep(3);
        return;
      }
    } else if (formData.subscription_status === 'active' && !formData.billing_cycle) {
      setError('Step 3: Billing cycle is required');
      setCurrentStep(3);
      return;
    }

    const siteIssue = sitesProblem();
    if (siteIssue) {
      setError(`Step 1: ${siteIssue}`);
      setCurrentStep(1);
      return;
    }

    // Account owner validation
    if (!owner.id) {
      if (!owner.name.trim()) {
        setError('Step 4: Please enter the account owner\'s full name');
        setCurrentStep(4);
        return;
      }
      if (owner.creation_type === 'link' && !owner.email.trim()) {
        setError('Step 4: An email address is required for the owner when using an invitation link');
        setCurrentStep(4);
        return;
      }
      const pinIssue = owner.creation_type === 'manual' ? pinProblem(owner.pin) : null;
      if (pinIssue) {
        setError(`Step 4: ${pinIssue}`);
        setCurrentStep(4);
        return;
      }
    } else if (!owner.pin_set && owner.pin) {
      // A first PIN for an owner who has none yet (e.g. a seeded one).
      const pinIssue = pinProblem(owner.pin);
      if (pinIssue) {
        setError(`Step 4: ${pinIssue}`);
        setCurrentStep(4);
        return;
      }
    }

    try {
      setSaving(true);
      // A care-home-only module is never saved for a domiciliary organisation,
      // even if it was ticked before the type was switched.
      const careHomeOnlyIds = availableModules
        .filter((m) => CARE_HOME_ONLY_MENU_CODES.includes(m.code))
        .map((m) => m.id);
      const submissionData = {
        ...formData,
        selectedMenuIds: formData.organization_type === 'domiciliary'
          ? formData.selectedMenuIds.filter((menuId) => !careHomeOnlyIds.includes(menuId))
          : formData.selectedMenuIds,
        // Site 1 first. Sent only for a group; a single-site organisation has none.
        ...(formData.setup_type === 'multi' && {
          sites: sites.map((site) => ({
            ...(site.id && { id: site.id }),
            name: site.name.trim(),
            is_active: site.is_active !== false,
          })),
        }),
        owner: {
          id: owner.id || undefined,
          name: owner.name.trim(),
          email: owner.email.trim(),
          phone: owner.phone,
          creation_type: owner.creation_type,
          pin: owner.pin || undefined,
          send_link: owner.send_link || undefined,
        },
      };

      console.log('Submitting Organization Data:', submissionData);

      let response;
      if (isEditMode) {
        response = await organizationService.updateOrganization(id, submissionData);
      } else {
        response = await organizationService.createOrganization(submissionData);
      }

      if (response.success) {
        setSuccess(true);
        setTimeout(() => navigate('/organizations'), 1500);
      } else {
        setError(response.message || `Failed to ${isEditMode ? 'update' : 'create'} organization`);
      }
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} organization:`, err);
      setError(err.message || `Failed to ${isEditMode ? 'update' : 'create'} organization`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12 w-full animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex items-center gap-4 mb-2">
        <button
          onClick={() => navigate('/organizations')}
          className="p-2 hover:bg-white dark:hover:bg-[#31373f] rounded-xl transition shadow-sm border border-transparent hover:border-gray-200 dark:hover:border-gray-800"
        >
          <ArrowLeft className="w-5 h-5 text-gray-700 dark:text-gray-200" />
        </button>
        <div>
          <h1 className="text-[24px] font-semibold text-[#1E293B] dark:text-gray-200 tracking-tight">
            {isEditMode ? 'Edit Organization' : 'Add Organization'}
          </h1>
          <p className="text-[14px] text-[#64748B] dark:text-gray-400 mt-0.5">
             {isEditMode ? 'Update profiles and operational data' : 'Set up a new service provider account'}
          </p>
        </div>
      </div>

      <div className="w-full">
        <div className="w-full">
          {/* Main Wizard Area */}
          <div className="bg-white dark:bg-[#262b33] rounded-[16px] shadow-sm border border-[#E2E8F0] dark:border-gray-800 p-6 lg:p-8">
            
            {/* Stepper Header */}
            <div className="flex items-center justify-between mb-8 pb-8 border-b border-[#E2E8F0] dark:border-gray-800">
              {steps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div className={`flex items-center gap-3 ${currentStep !== step.id ? 'opacity-70' : 'opacity-100'}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-[14px] transition-all duration-300 ${
                      currentStep > step.id ? 'bg-[#2563EB] text-white' :
                      currentStep === step.id ? 'bg-[#2563EB] text-white ring-4 ring-[#EFF6FF]' :
                      'bg-[#F1F5F9] text-[#94A3B8] dark:bg-[#31373f] dark:text-gray-500'
                    }`}>
                      {currentStep > step.id ? <Check className="w-5 h-5" /> : step.id}
                    </div>
                    <div className="hidden sm:block">
                      <p className={`text-[14px] font-semibold ${currentStep >= step.id ? 'text-[#1E293B] dark:text-gray-200' : 'text-[#94A3B8] dark:text-gray-500'}`}>
                        {step.label}
                      </p>
                      <p className="text-[12px] text-[#64748B] dark:text-gray-400">{step.desc}</p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="flex-1 h-1 mx-4 sm:mx-8 rounded-full bg-[#F1F5F9] dark:bg-[#31373f] overflow-hidden">
                      <div
                        className="h-full bg-[#2563EB] transition-all duration-500 ease-in-out"
                        style={{ width: currentStep > step.id ? '100%' : '0%' }}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Alerts */}
            {success && (
              <div className="mb-6 bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-900/50 text-emerald-800 dark:text-emerald-400 px-5 py-4 rounded-2xl text-[14px] font-semibold flex items-center gap-3 animate-in fade-in zoom-in duration-300">
                <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white">
                   <Check className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-bold text-[15px]">Successfully Saved!</p>
                  <p className="text-emerald-600 dark:text-emerald-400 font-normal">Details for <strong>{formData.name}</strong> have been saved.</p>
                </div>
              </div>
            )}



            <form 
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                  e.preventDefault();
                }
              }}
              className="min-h-[350px]"
            >
              
              {/* STEP 1: Basic Information */}
              {currentStep === 1 && (
                <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-[#2563EB] font-bold text-sm">1.1</div>
                    <h3 className="text-[16px] font-semibold text-[#1E293B] dark:text-gray-200">Basic Organization Profile</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6">
                    <div className="lg:col-span-2">
                      <label className="block text-[13px] font-medium text-[#475569] dark:text-gray-400 mb-1.5">Organization Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="e.g. Sunshine Care Home"
                        className="w-full px-4 py-2.5 border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-[#2563EB]/10 outline-none text-[14px] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                      />
                    </div>

                    <div>
                      <label className="block text-[13px] font-medium text-[#475569] dark:text-gray-400 mb-1.5">Organization Type <span className="text-red-500">*</span></label>
                      <Select
                        value={formData.organization_type}
                        onChange={(v) => { setFormData(prev => ({ ...prev, organization_type: v })); setError(''); }}
                        options={[
                          { value: 'carehome', label: 'Care Home' },
                          { value: 'domiciliary', label: 'Domiciliary Service' },
                        ]}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-[13px] font-medium text-[#475569] dark:text-gray-400 mb-1.5">Setup Type</label>
                      <Select
                        value={formData.setup_type}
                        onChange={handleSetupTypeChange}
                        options={[
                          { value: 'single', label: 'Single Site' },
                          { value: 'multi', label: 'Multi Site Group' },
                        ]}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-[13px] font-medium text-[#475569] dark:text-gray-400 mb-1.5">Official Email <span className="text-red-500">*</span></label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="contact@org.com"
                        className="w-full px-4 py-2.5 border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-[#2563EB]/10 outline-none text-[14px] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                      />
                    </div>

                    <div>
                      <label className="block text-[13px] font-medium text-[#475569] dark:text-gray-400 mb-1.5">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+44 000 000 000"
                        className="w-full px-4 py-2.5 border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-[#2563EB]/10 outline-none text-[14px] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                      />
                    </div>

                    <div>
                      <label className="block text-[13px] font-medium text-[#475569] dark:text-gray-400 mb-1.5">CQC Registration Number</label>
                      <input
                        type="text"
                        name="cqc_registration_number"
                        value={formData.cqc_registration_number}
                        onChange={handleChange}
                        placeholder="e.g. 1-123456789"
                        className="w-full px-4 py-2.5 border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-[#2563EB]/10 outline-none text-[14px] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-[13px] font-medium text-[#475569] dark:text-gray-400 mb-1.5">Full Address</label>
                      <textarea
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        rows="3"
                        placeholder="Street, City, Postcode"
                        className="w-full px-4 py-2.5 border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-[#2563EB]/10 outline-none text-[14px] resize-none dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                      />
                    </div>
                  </div>

                  {/* Multi Site Group: the sites the owner will switch between. */}
                  {formData.setup_type === 'multi' && (
                    <div className="space-y-4 pt-2">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-[#2563EB] font-bold text-sm">1.2</div>
                          <div>
                            <h3 className="text-[16px] font-semibold text-[#1E293B] dark:text-gray-200">Sites</h3>
                            <p className="text-[12px] text-[#64748B] dark:text-gray-400">
                              The owner picks a site from the top bar and sees that site's clients, staff and everything else.
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSites((prev) => [...prev, blankSite()])}
                          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#2563EB] hover:text-blue-700 bg-blue-50 dark:bg-blue-900/20 px-4 py-2 rounded-xl transition-all"
                        >
                          <Plus className="w-4 h-4" />
                          Add new site
                        </button>
                      </div>

                      <div className="space-y-3">
                        {sites.map((site, index) => (
                          <div
                            key={site.id || `new-${index}`}
                            className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-xl border ${site.is_active === false
                              ? 'border-dashed border-[#CBD5E1] dark:border-gray-700 bg-[#F8FAFC] dark:bg-[#20242c] opacity-70'
                              : 'border-[#E2E8F0] dark:border-gray-800 bg-white dark:bg-[#31373f]'
                            }`}
                          >
                            <span className="shrink-0 text-[11px] font-bold uppercase tracking-wide text-[#64748B] dark:text-gray-400 sm:w-16">
                              Site {index + 1}
                            </span>
                            <input
                              type="text"
                              value={site.name}
                              onChange={(e) => updateSite(index, { name: e.target.value })}
                              placeholder={index === 0 ? 'e.g. Oak House (main site)' : 'e.g. Elm House'}
                              className="flex-1 min-w-0 px-4 py-2 border border-[#CBD5E1] rounded-xl outline-none focus:ring-2 focus:ring-[#2563EB]/10 text-[14px] bg-white dark:bg-[#262b33] dark:text-gray-200 dark:border-gray-700"
                            />
                            {index === 0 ? (
                              <span className="shrink-0 text-[11px] font-bold text-[#2563EB] bg-blue-50 dark:bg-blue-900/20 px-2.5 py-1 rounded-full">Main site</span>
                            ) : site.id ? (
                              // A saved site is switched off, never deleted — its records stay.
                              <button
                                type="button"
                                onClick={() => updateSite(index, { is_active: site.is_active === false })}
                                className={`shrink-0 text-[12px] font-bold px-3 py-1.5 rounded-lg border transition ${site.is_active === false
                                  ? 'text-emerald-700 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:border-emerald-900/50 dark:text-emerald-400'
                                  : 'text-[#64748B] border-[#E2E8F0] hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-[#383e47]'
                                }`}
                              >
                                {site.is_active === false ? 'Switch on' : 'Switch off'}
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setSites((prev) => prev.filter((_, i) => i !== index))}
                                disabled={sites.length <= 1}
                                aria-label="Remove site"
                                className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg border border-[#E2E8F0] dark:border-gray-700 text-[#94A3B8] hover:text-rose-500 disabled:opacity-40 disabled:hover:text-[#94A3B8] transition"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      {sites.some((site) => site.id && site.is_active === false) && (
                        <p className="text-[12px] text-[#64748B] dark:text-gray-400">
                          A switched-off site disappears from everyone's site list. Its clients, staff and records are kept and come back if you switch it on again.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* STEP 2: Capacity & Operational Data */}
              {currentStep === 2 && (
                <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-[#2563EB] font-bold text-sm">2.1</div>
                    <h3 className="text-[16px] font-semibold text-[#1E293B] dark:text-gray-200">Capacity & Service Details</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6">
                    {formData.organization_type === 'carehome' ? (
                      <>
                        <div className="p-4 bg-gray-50 dark:bg-[#20242c] rounded-2xl border border-gray-100 dark:border-gray-800">
                          <label className="block text-[11px] font-bold text-[#64748B] dark:text-gray-400 uppercase mb-2">Residential Beds</label>
                          <input
                            type="number"
                            name="residential_beds"
                            value={formData.residential_beds}
                            onChange={handleChange}
                            className="w-full px-4 py-2 bg-white border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-blue-100 outline-none text-[14px] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                          />
                        </div>
                        <div className="p-4 bg-gray-50 dark:bg-[#20242c] rounded-2xl border border-gray-100 dark:border-gray-800">
                          <label className="block text-[11px] font-bold text-[#64748B] dark:text-gray-400 uppercase mb-2">Nursing Beds</label>
                          <input
                            type="number"
                            name="nursing_beds"
                            value={formData.nursing_beds}
                            onChange={handleChange}
                            className="w-full px-4 py-2 bg-white border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-blue-100 outline-none text-[14px] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                          />
                        </div>
                        <div className="p-4 bg-gray-50 dark:bg-[#20242c] rounded-2xl border border-gray-100 dark:border-gray-800">
                          <label className="block text-[11px] font-bold text-[#64748B] dark:text-gray-400 uppercase mb-2">Dementia Beds</label>
                          <input
                            type="number"
                            name="dementia_beds"
                            value={formData.dementia_beds}
                            onChange={handleChange}
                            className="w-full px-4 py-2 bg-white border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-blue-100 outline-none text-[14px] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                          />
                        </div>
                        <div className="p-4 bg-gray-50 dark:bg-[#20242c] rounded-2xl border border-gray-100 dark:border-gray-800">
                          <label className="block text-[11px] font-bold text-[#64748B] dark:text-gray-400 uppercase mb-2">Current Residents</label>
                          <input
                            type="number"
                            name="current_residents"
                            value={formData.current_residents}
                            onChange={handleChange}
                            className="w-full px-4 py-2 bg-white border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-green-100 outline-none text-[14px] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                          />
                        </div>
                        <div className="p-4 bg-gray-50 dark:bg-[#20242c] rounded-2xl border border-gray-100 dark:border-gray-800">
                          <label className="block text-[11px] font-bold text-[#64748B] dark:text-gray-400 uppercase mb-2">Staff Count</label>
                          <input
                            type="number"
                            name="current_staff"
                            value={formData.current_staff}
                            onChange={handleChange}
                            className="w-full px-4 py-2 bg-white border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-amber-100 outline-none text-[14px] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                          />
                        </div>
                        <div>
                          <label className="block text-[13px] font-medium text-[#475569] dark:text-gray-400 mb-1.5">Primary Care Category</label>
                          <input
                            type="text"
                            name="primary_care_category"
                            value={formData.primary_care_category}
                            onChange={handleChange}
                            placeholder="e.g. Elderly / Physical Disability"
                            className="w-full px-4 py-2.5 border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-[#2563EB]/10 outline-none text-[14px] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="p-4 bg-gray-50 dark:bg-[#20242c] rounded-2xl border border-gray-100 dark:border-gray-800">
                          <label className="block text-[11px] font-bold text-[#64748B] dark:text-gray-400 uppercase mb-2">Current Service Users <span className="text-red-500">*</span></label>
                          <input
                            type="number"
                            name="current_service_users"
                            value={formData.current_service_users}
                            onChange={handleChange}
                            className="w-full px-4 py-2 bg-white border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-blue-100 outline-none text-[14px] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                          />
                        </div>
                        <div className="p-4 bg-gray-50 dark:bg-[#20242c] rounded-2xl border border-gray-100 dark:border-gray-800">
                          <label className="block text-[11px] font-bold text-[#64748B] dark:text-gray-400 uppercase mb-2">Care Workers</label>
                          <input
                            type="number"
                            name="care_workers"
                            value={formData.care_workers}
                            onChange={handleChange}
                            className="w-full px-4 py-2 bg-white border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-blue-100 outline-none text-[14px] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                          />
                        </div>
                        <div className="p-4 bg-gray-50 dark:bg-[#20242c] rounded-2xl border border-gray-100 dark:border-gray-800">
                          <label className="block text-[11px] font-bold text-[#64748B] dark:text-gray-400 uppercase mb-2">Average Visits Per Day</label>
                          <input
                            type="number"
                            name="avg_visits_per_day"
                            value={formData.avg_visits_per_day}
                            onChange={handleChange}
                            className="w-full px-4 py-2 bg-white border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-blue-100 outline-none text-[14px] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                          />
                        </div>
                        <div>
                          <label className="block text-[13px] font-medium text-[#475569] dark:text-gray-400 mb-1.5">Operating Area</label>
                          <input
                            type="text"
                            name="operating_area"
                            value={formData.operating_area}
                            onChange={handleChange}
                            placeholder="e.g. Greater London"
                            className="w-full px-4 py-2.5 border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-[#2563EB]/10 outline-none text-[14px] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                          />
                        </div>
                        <div>
                          <label className="block text-[13px] font-medium text-[#475569] dark:text-gray-400 mb-1.5">Visit Model</label>
                          <Select
                            value={formData.visit_model}
                            onChange={(v) => { setFormData(prev => ({ ...prev, visit_model: v })); setError(''); }}
                            options={[
                              { value: 'hourly_care', label: 'Hourly care' },
                              { value: 'live_in_care', label: 'Live in care' },
                              { value: 'mixed_model', label: 'Mixed model' },
                            ]}
                            placeholder="Select Model"
                            className="w-full"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* STEP 3: Plan Selection */}
              {currentStep === 3 && (
                <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-[#2563EB] font-bold text-sm">3.1</div>
                    <h3 className="text-[16px] font-semibold text-[#1E293B] dark:text-gray-200">Select Subscription Plan</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {plans.map((plan) => (
                      <div 
                        key={plan.id}
                        onClick={() => setFormData(prev => ({ ...prev, plan_id: plan.id }))}
                        className={`relative p-6 rounded-2xl border-2 transition-all cursor-pointer group ${
                          formData.plan_id === plan.id 
                            ? 'border-[#2563EB] bg-blue-50/30 shadow-md' 
                            : 'border-[#E2E8F0] dark:border-gray-800 bg-white dark:bg-[#262b33] hover:border-blue-200 hover:shadow-sm'
                        }`}
                      >
                        {formData.plan_id === plan.id && (
                          <div className="absolute top-4 right-4 w-6 h-6 bg-[#2563EB] rounded-full flex items-center justify-center text-white shadow-sm">
                            <Check className="w-4 h-4" />
                          </div>
                        )}
                        
                        <div className={`w-12 h-12 rounded-xl mb-4 flex items-center justify-center transition-colors ${
                          formData.plan_id === plan.id ? 'bg-[#2563EB] text-white' : 'bg-[#F1F5F9] dark:bg-[#31373f] text-[#64748B] dark:text-gray-400 group-hover:bg-blue-100 group-hover:text-blue-600'
                        }`}>
                          <Building2 className="w-6 h-6" />
                        </div>
                        
                        <h4 className="text-[18px] font-bold text-[#1E293B] dark:text-gray-200 mb-1">{plan.name}</h4>
                        <div className="flex items-baseline gap-1 mb-4">
                          <span className="text-[24px] font-extrabold text-[#1E293B] dark:text-gray-200">£{plan.price}</span>
                          <span className="text-[12px] text-[#64748B] dark:text-gray-400">/month</span>
                        </div>
                        
                        <p className="text-[13px] text-[#64748B] dark:text-gray-400 mb-6 line-clamp-2 leading-relaxed">
                          {plan.description || `Optimized for organizations needing up to ${plan.max_users || 'unlimited'} users.`}
                        </p>
                        
                        <div className="space-y-3">
                           <div className="flex items-center gap-2 text-[12px] font-medium text-[#475569] dark:text-gray-400">
                              <div className="w-1.5 h-1.5 rounded-full bg-blue-400"></div>
                              <span>{plan.max_users === 0 ? 'Unlimited' : plan.max_users} Users</span>
                           </div>
                           <div className="flex items-center gap-2 text-[12px] font-medium text-[#475569] dark:text-gray-400">
                              <div className="w-1.5 h-1.5 rounded-full bg-blue-400"></div>
                              <span>All Core Features</span>
                           </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {plans.length === 0 && (
                    <div className="p-12 text-center border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-2xl">
                       <Loader className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
                       <p className="text-gray-500 dark:text-gray-400 font-medium">Loading available plans...</p>
                    </div>
                  )}

                  {formData.plan_id && (
                    <div className="space-y-6 animate-in slide-in-from-top-4 duration-500">
                      <div className="flex items-center gap-3 mt-8">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-[#2563EB] font-bold text-sm">3.2</div>
                        <h3 className="text-[16px] font-semibold text-[#1E293B] dark:text-gray-200">Subscription Details</h3>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white dark:bg-[#262b33] p-6 rounded-2xl border border-[#E2E8F0] dark:border-gray-800">
                        <div>
                          <label className="block text-[13px] font-medium text-[#475569] dark:text-gray-400 mb-1.5">Subscription Status</label>
                          <Select
                            value={formData.subscription_status}
                            onChange={(v) => { setFormData(prev => ({ ...prev, subscription_status: v })); setError(''); }}
                            options={[
                              { value: 'trial', label: 'Trial Period' },
                              { value: 'active', label: 'Active Subscription' },
                            ]}
                            className="w-full"
                          />
                        </div>

                        {formData.subscription_status === 'active' ? (
                          <div>
                            <label className="block text-[13px] font-medium text-[#475569] dark:text-gray-400 mb-1.5">Billing Cycle</label>
                            <Select
                              value={formData.billing_cycle}
                              onChange={(v) => { setFormData(prev => ({ ...prev, billing_cycle: v })); setError(''); }}
                              options={[
                                { value: 'monthly', label: 'Monthly' },
                                { value: 'quarterly', label: 'Quarterly' },
                                { value: 'yearly', label: 'Yearly' },
                              ]}
                              className="w-full"
                            />
                          </div>
                        ) : (
                          <>
                            <div>
                              <label className="block text-[13px] font-medium text-[#475569] dark:text-gray-400 mb-1.5">Trial Start Date</label>
                              <input
                                type="date"
                                name="trial_start_date"
                                value={formData.trial_start_date}
                                onChange={handleChange}
                                className="w-full px-4 py-2.5 border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-[#2563EB]/10 outline-none text-[14px] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                              />
                            </div>
                            <div>
                              <label className="block text-[13px] font-medium text-[#475569] dark:text-gray-400 mb-1.5">Trial End Date</label>
                              <input
                                type="date"
                                name="trial_end_date"
                                value={formData.trial_end_date}
                                onChange={handleChange}
                                className="w-full px-4 py-2.5 border border-[#CBD5E1] rounded-xl focus:ring-2 focus:ring-[#2563EB]/10 outline-none text-[14px] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                              />
                            </div>
                          </>
                        )}
                      </div>

                      {/* Module Selection */}
                      <div className="space-y-6 animate-in slide-in-from-top-4 duration-500">
                        <div className="flex items-center gap-3 mt-8">
                          <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-[#2563EB] font-bold text-sm">3.3</div>
                          <h3 className="text-[16px] font-semibold text-[#1E293B] dark:text-gray-200">Module Access Control</h3>
                        </div>

                        <div className="bg-white dark:bg-[#262b33] p-6 rounded-2xl border border-[#E2E8F0] dark:border-gray-800">
                          <p className="text-[13px] text-[#64748B] dark:text-gray-400 mb-6">
                            Select the modules that should be available to the administrative users of this organization.
                          </p>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* Care-home-only modules (the Care Planner) are not offered to a domiciliary organisation. */}
                            {withoutCareHomeOnlyMenus(availableModules, formData.organization_type).map((module) => (
                              <div 
                                key={module.id}
                                onClick={() => handleModuleToggle(module.id)}
                                className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                                  formData.selectedMenuIds.includes(module.id)
                                    ? 'border-[#2563EB] bg-blue-50/50'
                                    : 'border-[#F1F5F9] dark:border-gray-800 bg-white dark:bg-[#31373f] hover:border-blue-100'
                                }`}
                              >
                                <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                                  formData.selectedMenuIds.includes(module.id)
                                    ? 'bg-[#2563EB] border-[#2563EB] text-white'
                                    : 'bg-white dark:bg-[#31373f] border-[#CBD5E1] dark:border-gray-700'
                                }`}>
                                  {formData.selectedMenuIds.includes(module.id) && <Check className="w-3.5 h-3.5" />}
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-[14px] font-semibold text-[#1E293B] dark:text-gray-200 leading-tight">{module.name}</span>
                                  <span className="text-[11px] text-[#94A3B8] dark:text-gray-500 font-mono uppercase mt-0.5">{module.code}</span>
                                </div>
                              </div>
                            ))}
                          </div>

                          {availableModules.length === 0 && (
                            <div className="py-8 text-center bg-[#F8FAFC] dark:bg-[#20242c] rounded-xl border border-dashed border-[#E2E8F0] dark:border-gray-800">
                               <Loader className="w-5 h-5 animate-spin text-blue-500 mx-auto mb-2" />
                               <p className="text-[12px] text-[#64748B] dark:text-gray-400">Loading available modules...</p>
                            </div>
                          )}
                          
                          <div className="mt-6 flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/10 rounded-xl border border-amber-100 dark:border-amber-900/50">
                            <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                            <p className="text-[11px] text-amber-700 dark:text-amber-400 leading-tight">
                              <strong>Note:</strong> Selected modules are given to the account owner and every manager they add.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* STEP 4: Account Owner */}
              {currentStep === 4 && (
                <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
                  <div className="flex items-center gap-3 border-b border-gray-100 dark:border-gray-800 pb-4">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-[#2563EB] font-bold text-sm">4.1</div>
                    <h3 className="text-[16px] font-semibold text-[#1E293B] dark:text-gray-200">Account Owner</h3>
                  </div>

                  {/* Who creates whom, and the login id rule */}
                  <div className="flex items-start gap-3 p-3 bg-blue-50/60 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/50 rounded-xl">
                    <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                    <div className="text-[12px] text-blue-800 dark:text-blue-400 leading-snug">
                      <p className="font-bold">You create the owner only. The owner adds managers from Staff Management, and managers add staff.</p>
                      <p className="text-blue-700 dark:text-blue-400 mt-0.5">
                        Owner login IDs: care home <span className="font-mono font-bold">OCH1001, OCH1002…</span> &nbsp;·&nbsp;
                        domiciliary <span className="font-mono font-bold">OHC1001, OHC1002…</span>
                        <br />Staff, admins and managers they add get <span className="font-mono font-bold">CH</span> / <span className="font-mono font-bold">HC</span> + 6 digits, e.g. <span className="font-mono font-bold">CH742816</span>.
                      </p>
                    </div>
                  </div>

                  <div className="p-6 border border-[#E2E8F0] dark:border-gray-800 rounded-[16px] bg-[#F8FAFC] dark:bg-[#20242c]">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6">
                      <div>
                        <label className="block text-[11px] font-bold text-[#64748B] dark:text-gray-400 uppercase mb-1.5">Full Name <span className="text-red-500">*</span></label>
                        <input
                          type="text"
                          name="name"
                          value={owner.name}
                          onChange={handleOwnerChange}
                          className="w-full px-4 py-2 bg-white border border-[#CBD5E1] rounded-xl text-[14px] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-[#64748B] dark:text-gray-400 uppercase mb-1.5">
                          Owner Login ID
                          {!owner.id && <span className="ml-2 text-[10px] font-bold text-[#2563EB] bg-blue-50 dark:bg-blue-900/20 px-2 py-0.5 rounded-full normal-case tracking-normal">Auto</span>}
                        </label>
                        <input
                          type="text"
                          value={owner.user_id}
                          disabled
                          placeholder={formData.organization_type === 'carehome' ? 'OCH1001' : 'OHC1001'}
                          className="w-full px-4 py-2 bg-gray-50 border border-[#CBD5E1] rounded-xl text-[14px] font-mono opacity-80 cursor-not-allowed dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                        />
                      </div>

                      {/* New owner: choose how their PIN is set. Existing owner
                          with no PIN yet (e.g. seeded): set one or send a link. */}
                      {!owner.id ? (
                        <div>
                          <label className="block text-[11px] font-bold text-[#64748B] dark:text-gray-400 uppercase mb-1.5">Access Type</label>
                          <Select
                            value={owner.creation_type || 'manual'}
                            onChange={(v) => setOwner((prev) => ({ ...prev, creation_type: v }))}
                            options={[
                              { value: 'manual', label: 'Manual PIN' },
                              { value: 'link', label: 'Invitation Link' },
                            ]}
                            className="w-full"
                          />
                        </div>
                      ) : !owner.pin_set ? (
                        <div>
                          <label className="block text-[11px] font-bold text-[#64748B] dark:text-gray-400 uppercase mb-1.5">Sign-in</label>
                          <Select
                            value={owner.send_link ? 'link' : 'manual'}
                            onChange={(v) => setOwner((prev) => ({ ...prev, send_link: v === 'link', pin: v === 'link' ? '' : prev.pin }))}
                            options={[
                              { value: 'manual', label: 'Set a PIN now' },
                              { value: 'link', label: 'Email a set-PIN link' },
                            ]}
                            className="w-full"
                          />
                          <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">No PIN yet — they cannot sign in until one is set.</p>
                        </div>
                      ) : null}

                      {((!owner.id && owner.creation_type !== 'link') || (owner.id && !owner.pin_set && !owner.send_link)) && (
                        <div>
                          <label className="block text-[11px] font-bold text-[#64748B] dark:text-gray-400 uppercase mb-1.5">
                            Security PIN {!owner.id && '*'} (6 digits)
                          </label>
                          <input
                            type="password"
                            name="pin"
                            inputMode="numeric"
                            maxLength={6}
                            placeholder="6 digits"
                            value={owner.pin}
                            onChange={(e) => handleOwnerChange({
                              target: { name: 'pin', value: e.target.value.replace(/\D/g, '').slice(0, 6) },
                            })}
                            className="w-full px-4 py-2 bg-white border border-[#CBD5E1] rounded-xl text-[14px] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                          />
                        </div>
                      )}

                      <div>
                        <label className="block text-[11px] font-bold text-[#64748B] dark:text-gray-400 uppercase mb-1.5">Email Address {(owner.creation_type === 'link' || owner.send_link) && <span className="text-red-500">*</span>}</label>
                        <input
                          type="email"
                          name="email"
                          value={owner.email}
                          onChange={handleOwnerChange}
                          placeholder="Required for invitation"
                          className="w-full px-4 py-2 bg-white border border-[#CBD5E1] rounded-xl text-[14px] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-[#64748B] dark:text-gray-400 uppercase mb-1.5">Phone Number</label>
                        <input
                          type="tel"
                          name="phone"
                          value={owner.phone}
                          onChange={handleOwnerChange}
                          className="w-full px-4 py-2 bg-white border border-[#CBD5E1] rounded-xl text-[14px] dark:bg-[#31373f] dark:text-gray-200 dark:border-gray-700"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Secondary Error Display for Mobile/Small Screens */}
              {error && (
                <div className="mt-6 p-3 bg-rose-50 dark:bg-rose-900/10 border border-rose-100 dark:border-rose-900/50 rounded-xl flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <AlertCircle className="w-4 h-4 text-rose-600 dark:text-rose-400 shrink-0" />
                  <p className="text-[13px] text-rose-700 dark:text-rose-400 font-medium">{error}</p>
                </div>
              )}

              {/* Wizard Controls */}
              <div className="flex items-center justify-between mt-10 pt-6 border-t border-[#E2E8F0] dark:border-gray-800">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={currentStep === 1 ? () => navigate('/organizations') : handlePrev}
                  leftIcon={currentStep === 1 ? undefined : <ChevronLeft className="w-4 h-4" />}
                >
                  {currentStep === 1 ? 'Cancel' : 'Back'}
                </Button>

                <div className="flex items-center gap-3">
                  {currentStep < totalSteps ? (
                    <Button
                      type="button"
                      variant="primary"
                      onClick={handleNext}
                      rightIcon={<ChevronRight className="w-4 h-4" />}
                    >
                      Next Step
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="primary"
                      onClick={handleSubmit}
                      loading={saving}
                      leftIcon={<Save className="w-4 h-4" />}
                    >
                      {isEditMode ? 'Save Changes' : 'Complete Setup'}
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganization;
